// 2026-07-29 - FormuLearning - AI 예측 신뢰도·적용범위 표시 (이슈 #51)
// 파일 위치: frontend/src/features/step2/PredictionQuality.tsx
//
// OPERA 는 예측값과 함께 적용범위(AD)·신뢰도 지수를 산출한다. 엔드포인트별 학습셋
// 규모 편차가 커(융점 22,554건 ↔ KOA 270건) 예측값만으로는 신뢰 여부를 판단할 수
// 없으므로 화면에 함께 노출한다 (BR-S2-06).
//
// 2026-09-13 (M901-10, 이슈 #74): 배지 본문을 수치 → **등급 글자**로 바꾼다. 회의록(8/19) 7번
// *"양호 보통 이런 정도만 보여주고, 누르면 지금 그대로 수치"* — 평상시 = 글자, 클릭 = 수치.
// 종전 구현은 배지에 수치를, 팝오버에 글자를 두어 요구와 정반대였다(M819 v1.1 §3.3).
// 2026-11-03 (이슈 #104): Lipinski 행을 화면에서 빼면서 `LipinskiDetail` 삭제.

import Popover from '../../components/common/Popover'
import type { PropertyQuality } from '../../api/step2'

/** 신뢰도 구간 — 0.7 이상 양호 / 0.5 이상 보통 / 미만 낮음 */
const HIGH = 0.7
const MID = 0.5

const WARN = { color: 'var(--color-warn)', bg: 'color-mix(in srgb, var(--color-warn) 14%, transparent)' }
const GOOD = { color: 'var(--color-accent-teal)', bg: 'var(--bg-chip-teal)' }
const NEUTRAL = { color: 'var(--color-text-secondary)', bg: 'var(--color-bg-panel)' }

/** 배지에 띄울 등급(`grade`)과 팝오버 제목(`title`)을 함께 돌려준다. */
function tone(q: PropertyQuality): { color: string; bg: string; grade: string; title: string } {
  // 적용범위 밖이면 신뢰도와 무관하게 경고로 취급한다 — 학습셋이 다루지 않는 구조다.
  if (q.ad === false) {
    return { ...WARN, grade: '적용범위 밖', title: '적용범위 밖' }
  }
  // 지수가 없는데 «낮음»으로 적으면 근거 없는 판정이 된다 — 산출되지 않았음을 그대로 알린다.
  if (q.conf_index == null) {
    return { ...NEUTRAL, grade: '미산출', title: '신뢰도 미산출' }
  }
  if (q.conf_index >= HIGH) {
    return { ...GOOD, grade: '양호', title: '신뢰도 양호' }
  }
  if (q.conf_index >= MID) {
    return { ...NEUTRAL, grade: '보통', title: '신뢰도 보통' }
  }
  return { ...WARN, grade: '낮음', title: '신뢰도 낮음' }
}

function fmt(v: number | null | undefined, digits = 2): string {
  if (v == null) return '—'
  return Math.abs(v) >= 1e-3 || v === 0 ? v.toFixed(digits) : v.toExponential(1)
}

function Row({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex justify-between gap-3">
      <span className="text-text-secondary">{label}</span>
      <span className="font-medium">{value}</span>
    </div>
  )
}

export function QualityBadge({ quality }: { quality: PropertyQuality }) {
  const t = tone(quality)
  const badge = (
    <span
      className="ml-1 inline-flex items-center gap-0.5 rounded px-1 py-px text-[9px] font-medium leading-none"
      style={{ color: t.color, background: t.bg }}
    >
      {quality.ad === false && <span aria-hidden>⚠</span>}
      {t.grade}
    </span>
  )

  return (
    <Popover trigger={badge} label="예측 신뢰도 상세" width={250}>
      <div className="mb-1.5 font-semibold" style={{ color: t.color }}>
        {t.title}
      </div>
      <div className="space-y-0.5">
        <Row label="신뢰도" value={fmt(quality.conf_index)} />
        <Row label="적용범위 지수" value={fmt(quality.ad_index)} />
        {quality.range && (
          <Row
            label="예측범위"
            value={`${fmt(quality.range[0], 3)} ~ ${fmt(quality.range[1], 3)}${quality.unit ? ` ${quality.unit}` : ''}`}
          />
        )}
      </div>
      <div className="mt-1.5 border-t pt-1.5 text-text-secondary" style={{ borderColor: 'var(--border-subtle)' }}>
        {quality.ad === false
          ? '학습셋이 다루지 않는 구조 영역이라 값의 불확실성이 큽니다. 참고용으로만 쓰고 실측을 권장합니다.'
          : '학습셋 내 유사 구조 5건의 거리 가중평균으로 산출된 값입니다. 신뢰도는 이웃의 예측오차·적용범위·산포를 종합한 지표입니다.'}
      </div>
      <div className="mt-1 text-text-muted">
        판정 기준: 신뢰도 {HIGH} 이상 양호 · {MID} 이상 보통 · 미만 낮음 (적용범위 밖은 신뢰도와 무관하게 경고)
      </div>
    </Popover>
  )
}

function LegendChip({ style, children }: { style: { color: string; bg: string }; children: React.ReactNode }) {
  return (
    <span className="inline-flex items-center gap-0.5 rounded px-1 py-px" style={{ color: style.color, background: style.bg }}>
      {children}
    </span>
  )
}

/** 표 하단 범례 — 배지 글자가 무엇인지 한 줄로 알린다. 수치 기준은 팝오버가 갖는다(M901-10). */
export function QualityLegend({ source }: { source?: string }) {
  return (
    <div className="mt-1.5 flex flex-wrap items-center gap-x-3 gap-y-1 text-[9px] text-text-muted">
      <span>AI Prediction 우측 배지 = 예측 신뢰도 등급. <strong>배지를 클릭</strong>하면 신뢰도·적용범위 지수·예측범위 수치가 열립니다.</span>
      <span className="inline-flex items-center gap-1.5">
        <LegendChip style={GOOD}>양호</LegendChip>
        <LegendChip style={NEUTRAL}>보통</LegendChip>
        <LegendChip style={WARN}>낮음</LegendChip>
        <LegendChip style={WARN}>
          <span aria-hidden>⚠</span>적용범위 밖
        </LegendChip>
      </span>
      {source && <span className="ml-auto">예측 출처: {source}</span>}
    </div>
  )
}
